import { reportEvent } from '~/domains/analytics';

import type { GetPropsFiltersPayload } from '../services/interfaces/propsService/api';

export const reportQuickPicksFilters = (filters: GetPropsFiltersPayload) => {
  reportEvent('QuickPicks > Filter', {
    league: filters.league,
    propType: filters.type,
    gamesFiltered: filters.game,
  });
};

export const reportQuickPicksSearch = ({ resultCount }: { resultCount: number }) => {
  reportEvent('QuickPicks > Search', {
    resultCount,
  });
};

export const reportQuickPicksPlayerCardView = ({
  location,
}: {
  location: 'board' | 'entryDetail' | 'homePromo';
}) => {
  reportEvent('QuickPicks > View Player Card', {
    location,
  });
};

export const reportQuickPicksPicksView = ({
  location,
  numberOfPicks,
}: {
  location: 'board' | 'homePromo';
  numberOfPicks: number;
}) => {
  reportEvent('QuickPicks > View Picks', {
    numberOfPicks,
    location,
  });
};

export const reportQuickPicksPicksClose = ({ numberOfPicks }: { numberOfPicks: number }) => {
  reportEvent('QuickPicks > Close Picks', {
    numberOfPicks,
  });
};

export const reportQuickPicksPickMake = ({
  location,
}: {
  location: 'board' | 'playerCard' | 'homePromo';
}) => {
  reportEvent('QuickPicks > Make Pick', {
    location,
  });
};

export const reportQuickPicksAlternateModalView = ({
  numberOfPicks,
}: {
  numberOfPicks: number;
}) => {
  reportEvent('QuickPicks > View Alternate Modal', {
    numberOfPicks,
  });
};

export const reportQuickPicksAlternatePickMake = ({
  numberOfPicks,
  location,
}: {
  numberOfPicks: number;
  location: 'board' | 'playerCard';
}) => {
  reportEvent('QuickPicks > Make Alternate Pick', {
    numberOfPicks,
    location,
  });
};

export const reportQuickPicksPickRemove = ({
  location,
}: {
  location: 'board' | 'playerCard' | 'entryDetail' | 'homePromo';
}) => {
  reportEvent('QuickPicks > Remove Pick', {
    location,
  });
};

export const reportQuickPicksPickEdit = ({
  location,
}: {
  location: 'board' | 'playerCard' | 'entryDetail' | 'homePromo';
}) => {
  reportEvent('QuickPicks > Swap Pick Direction', {
    location,
  });
};

export const reportQuickPicksPicksChangeEntryFee = ({
  entryFee,
  numberOfPicks,
}: {
  entryFee: number;
  numberOfPicks: number;
}) => {
  reportEvent('QuickPicks > Change Entry Fee', {
    entryFee,
    numberOfPicks,
  });
};

export const reportQuickPicksEntrySubmit = ({
  entryFee,
  numberOfPicks,
  alternate,
  payoutType,
  protectedMultipler,
  isFirstSubmit,
  promotion,
}: {
  entryFee: number;
  numberOfPicks: number;
  alternate: boolean;
  payoutType?: 'max' | 'protected';
  protectedMultipler?: number;
  isFirstSubmit?: boolean;
  promotion?: Record<string, unknown>;
}) => {
  const eventName = isFirstSubmit
    ? 'QuickPicks > First Submit Entry'
    : 'QuickPicks > Submit Entry';

  reportEvent(eventName, {
    entryFee,
    numberOfPicks,
    alternate,
    af_revenue: entryFee,
    ...(promotion && { promotion }),
    ...(payoutType && { payoutType }),
    ...(protectedMultipler && { protectedMultipler }),
  });
};

export const reportQuickPicksEntryCancel = ({
  entryFee,
  numberOfPicks,
}: {
  entryFee: number;
  numberOfPicks: number;
}) => {
  reportEvent('QuickPicks > Cancel Entry', {
    entryFee,
    numberOfPicks,
  });
};

export const reportQuickPicksEntryDetailsView = ({
  location,
}: {
  location: 'active' | 'final' | 'standings' | 'my-picks';
}) => {
  reportEvent('QuickPicks > View Entry Details', {
    location,
  });
};

export const reportQuickPicksPicksAlert = ({
  numberOfChanges,
  propChanged,
  propRemoved,
}: {
  numberOfChanges: number;
  propChanged: boolean;
  propRemoved: boolean;
}) => {
  reportEvent('QuickPicks > Slip Alert', {
    numberOfChanges,
    propChanged,
    propRemoved,
  });
};

export const reportQuickPicksPicksReuse = ({ numberOfPicks }: { numberOfPicks: number }) => {
  reportEvent('QuickPicks > Reuse Picks', {
    numberOfPicks,
  });
};

export const reportQuickPicksCopySlipId = ({ slipId }: { slipId: string }) => {
  reportEvent('QuickPicks > Copy Slip ID', {
    slip_id: slipId,
  });
};

export const reportQuickPicksPromotionsViewAll = ({
  totalCount,
  displayedCount,
  hasSelectedBoost,
  hasSelectedVoucher,
}: {
  totalCount: number;
  displayedCount: number;
  hasSelectedBoost: boolean;
  hasSelectedVoucher: boolean;
}) => {
  reportEvent('QuickPicks > Promotions > View All', {
    totalCount,
    displayedCount,
    location: 'board',
    hasSelectedBoost,
    hasSelectedVoucher,
  });
};
